import { useState } from 'react'
import type { DeviationRow } from '@/services/api/effectivePlan'
import type { DirectorRequestDetail } from '@/types/director'
import { directorApi } from '@/services/api/director'
import { extractErrorMessage } from '@/utils/errorHandler'
import { abortSentence, caseSentenceAbortVisible, parentIsGone, releaseSentence } from './planFormat'

const COMMITTED = ['accepted', 'assigned', 'filled', 'booked']

function isLive(status: string | null): boolean {
  return status !== 'cancelled' && status !== 'director_removed'
}

function lessonClock(
  lesson: { id: string; start_time?: string | null; effective_start_time?: string | null },
  deviations: DeviationRow[],
): string {
  const hit = deviations.find(d => d.lesson_id === lesson.id)
  return (hit?.start || lesson.effective_start_time || lesson.start_time || '00:00').slice(0, 5)
}

function isOpen(lesson: DirectorRequestDetail['lessons'][number]): boolean {
  if (!isLive(lesson.status)) return false
  if (lesson.assigned_teacher_id && COMMITTED.includes(lesson.status ?? '')) return false
  return true
}

export function CasePlanActions({
  detail, deviations = [], readOnly = false, onChanged,
}: {
  detail: DirectorRequestDetail
  deviations?: DeviationRow[]
  readOnly?: boolean
  onChanged: () => void | Promise<void>
}) {
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  if (readOnly) return null
  if (!detail.conditional_approval) return null
  if (parentIsGone(detail.status)) return null

  const lastName = detail.requesting_teacher_last_name || detail.requesting_teacher_name || 'Lehrperson'
  const open = detail.lessons.filter(isOpen)
  const live = detail.lessons.filter(l => isLive(l.status))
  const losses = new Map<string, { name: string; times: string[] }>()
  for (const lesson of detail.lessons) {
    if (!lesson.assigned_teacher_id || !COMMITTED.includes(lesson.status ?? '')) continue
    const name = lesson.assigned_teacher_last_name || lesson.assigned_teacher_name || 'Stellvertretung'
    const start = lessonClock(lesson, deviations)
    const row = losses.get(lesson.assigned_teacher_id) ?? { name, times: [] }
    row.times.push(start)
    row.times.sort()
    losses.set(lesson.assigned_teacher_id, row)
  }
  const showAbort = caseSentenceAbortVisible(detail)

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
      await onChanged()
    } catch (err) {
      setError(extractErrorMessage(err, { loadError: 'Die Änderung ist fehlgeschlagen', saveError: 'Die Änderung ist fehlgeschlagen' }))
    } finally {
      setBusy(false)
    }
  }

  if (!open.length && !showAbort) return null

  return (
    <div className="flex flex-col gap-2" data-testid="case-plan-actions">
      {error ? <div className="rounded bg-red-100 px-3 py-2 text-sm text-red-700">{error}</div> : null}
      {open.map(lesson => {
        const start = lessonClock(lesson, deviations)
        return (
          <button
            key={lesson.id}
            type="button"
            disabled={busy}
            className="self-start rounded border border-gray-300 px-3 py-1.5 text-left text-sm hover:bg-gray-50 disabled:opacity-50"
            onClick={() => { void run(() => directorApi.releaseLesson(detail.id, lesson.id)) }}
          >
            {releaseSentence(start, lastName)}
          </button>
        )
      })}
      {showAbort ? (
        <button
          type="button"
          disabled={busy}
          className="self-start rounded border border-red-200 bg-red-50 px-3 py-1.5 text-left text-sm text-red-800 hover:bg-red-100 disabled:opacity-50"
          onClick={() => { void run(() => directorApi.abortSearch(detail.id, 'Suche abbrechen')) }}
        >
          {abortSentence(live.length, [...losses.values()])}
        </button>
      ) : null}
    </div>
  )
}
